import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type Selection } from 'react-aria-components';
import { useSelectedEnvironmentKey } from '@gonfalon/app-context/useSelectedEnvironmentKey';
import { useDebounce } from '@gonfalon/async/useDebounce';
import { msg } from '@gonfalon/messages/msg';
import { toCreateMetric, toCreateMetricGroup } from '@gonfalon/navigator/navigators';
import { useInfiniteMetricGroups } from '@gonfalon/rest-api/hooks/useInfiniteMetricGroups';
import { useInfiniteMetrics } from '@gonfalon/rest-api/hooks/useInfiniteMetrics';
import { useMetrics } from '@gonfalon/rest-api/hooks/useMetrics';
import type { Path } from '@gonfalon/router/isPath';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import type { SearchResultsData } from '@gonfalon/ui/ResourceSelector';
import { ResourceSelector, SearchCategories, SearchResults } from '@gonfalon/ui/ResourceSelector';
import { Button, Input, LinkButton, ListBoxItem, ProgressBar } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import clsx from 'clsx';

import { metricsSearchTracker } from '../../analytics';
import type { Metric, MetricGroupRep } from '../../types';
import { useMetricGroupsByKeys } from '../../useMetricGroupsByKeys';
import { useMetricsByKeys } from '../../useMetricsByKeys';

import {
  trackMetricSelectionCategorySelected,
  trackMetricSelectionCreateActionClicked,
  trackMetricSelectionFirstInteraction,
  trackMetricSelectionItemSelected,
  trackMetricSelectionSearchQueried,
  trackMetricSelectionUnselectAllClicked,
} from './analytics';
import {
  categorySupportsAllMetricsSearch,
  categorySupportsInfiniteScroll,
  getCategories,
  getDefaultCategory,
  getMetricGroupsQueryParams,
  getQueryParamsForCategory,
  METRICS_CATEGORIES,
} from './categories';
import { MetricDetailsPanel } from './MetricDetailsPanel';
import { MetricGroupDetailsPanel } from './MetricGroupDetailsPanel';
import { MetricGroupItemContent } from './MetricGroupItemContent';
import { MetricItemContent } from './MetricItemContent';
import { MetricsSearchEmptyState } from './MetricsSearchEmptyState';

import styles from './MetricsSearch.module.css';

// Type utilities for handling both metrics and metric groups
type MetricOrGroup = Metric | MetricGroupRep;

// Type guard to check if an item is a metric group
const isMetricGroup = (item: MetricOrGroup): item is MetricGroupRep => {
  return 'metrics' in item;
};

// Custom hook to load all default selected items (ensures they're always available)
const useDefaultSelectedItems = (defaultMetricKeys: string[], defaultGroupKeys: string[]) => {
  // Always fetch all default selected metrics (re-queries when keys change)
  const {
    metrics: defaultMetrics,
    isPending: metricsLoading,
    isError: metricsError,
  } = useMetricsByKeys(defaultMetricKeys);

  // Always fetch all default selected metric groups (re-queries when keys change)
  const {
    metricGroups: defaultGroups,
    isPending: groupsLoading,
    isError: groupsError,
  } = useMetricGroupsByKeys(defaultGroupKeys);

  return {
    defaultMetrics: defaultMetrics || [],
    defaultGroups: defaultGroups || [],
    isLoading: metricsLoading || groupsLoading,
    isError: metricsError || groupsError,
  };
};

export interface MetricSelection {
  selectedMetrics: Metric[];
  selectedMetricGroups: MetricGroupRep[];
}

export interface MetricsSearchProps {
  // Selection behavior
  selectionMode?: 'single' | 'multiple'; // Default: 'single'

  // Disable create
  disableCreate?: boolean;

  // Initial state (uncontrolled) - separate arrays for clarity
  defaultSelectedMetricKeys?: string[]; // Default: []
  defaultSelectedMetricGroupKeys?: string[]; // Default: []

  // Default category
  defaultCategory?: string; // Default: determined by getDefaultCategory function

  // Data filtering (component handles fetching internally)
  isUnavailable?: (item: Metric | MetricGroupRep) => string | undefined; // Return reason if unavailable, undefined if available
  allowMetrics?: boolean; // Default: true - when false, only shows metric groups category
  allowMetricGroups?: boolean; // Default: true - when false, excludes metric groups category
  allowWarehouseNativeMetrics?: boolean; // Default: false - when false, excludes warehouse native metrics category

  // Display options
  scopeByEnvironment?: boolean; // Default: false - when true, scopes last seen times by current environment

  // Event handlers
  onSelectionChange?: (selection: MetricSelection) => void;

  // On create handlers (to be used if overriding the default link out behavior)
  onCreateMetric?: () => void;
  onCreateMetricGroup?: () => void;

  /** When set, warehouse-native empty states only count metric data sources for this warehouse. */
  expectedIntegrationKey?: string;

  onDone?: () => void;

  /** When true, hides the Done button in the ResourceSelector footer (useful when the parent has its own action buttons, e.g. in a modal) */
  hideDoneButton?: boolean;
}

// Helper function to render create button/link (for header)
const renderCreateAction = (
  text: string,
  path: Path,
  onPress?: () => void,
  disabled?: boolean,
  showExternalIcon = true,
  onTrackFirstInteraction?: () => void,
) => {
  if (disabled) {
    return null;
  }

  const handlePress = () => {
    onTrackFirstInteraction?.();
    trackMetricSelectionCreateActionClicked();
    metricsSearchTracker.track('Clicked Create Button');

    if (onPress) {
      onPress();
    }
  };

  if (onPress) {
    return <Button onPress={handlePress}>{text}</Button>;
  }

  return (
    <LinkButton href={path} target="_blank" onPress={handlePress}>
      {text} {showExternalIcon && <Icon name="link-external" />}
    </LinkButton>
  );
};

// Custom hook to handle all data fetching logic with infinite queries
const useMetricsData = (
  projectKey: string,
  selectedCategory: string,
  debouncedSearchValue: string,
  allowMetrics: boolean,
  allowMetricGroups: boolean,
  releaseGuardrailsEnabled: boolean,
  includeMetricsReleasePolicyCount: boolean,
  includeMetricGroupsReleasePolicyCount: boolean,
) => {
  // Get query parameters for current category and search (without offset)
  const queryParams = useMemo(() => {
    if (
      selectedCategory === 'metric-groups' ||
      selectedCategory === 'funnel-groups' ||
      selectedCategory === 'currently-selected' ||
      selectedCategory === 'most-used' ||
      selectedCategory === 'recently-added' ||
      selectedCategory === 'release-guardrails'
    ) {
      return null;
    }
    return getQueryParamsForCategory(selectedCategory, debouncedSearchValue, 0, includeMetricsReleasePolicyCount); // offset handled by infinite query
  }, [selectedCategory, debouncedSearchValue, includeMetricsReleasePolicyCount]);

  const metricGroupsQueryParams = useMemo(() => {
    return selectedCategory !== 'metric-groups' && selectedCategory !== 'funnel-groups'
      ? null
      : getMetricGroupsQueryParams(selectedCategory, debouncedSearchValue, 0, includeMetricGroupsReleasePolicyCount);
  }, [selectedCategory, debouncedSearchValue, includeMetricGroupsReleasePolicyCount]);

  // Use infinite queries for paginated categories
  // Only enable when we have actual params to avoid fetching with null/undefined params
  const infiniteMetricsQuery = useInfiniteMetrics(
    {
      projectKey,
      params: queryParams ?? undefined,
    },
    {
      enabled: queryParams !== null,
      staleTime: 5 * 60 * 1000,
    } as Parameters<typeof useInfiniteMetrics>[1],
  );

  const infiniteMetricGroupsQuery = useInfiniteMetricGroups(
    {
      projectKey,
      params: metricGroupsQueryParams || undefined,
    },
    {
      enabled: metricGroupsQueryParams !== null,
      staleTime: 5 * 60 * 1000,
    } as Parameters<typeof useInfiniteMetricGroups>[1],
  );

  // When searching within a filtered category, also search across all metrics so
  // matches outside the category can be shown in a separate section
  const allMetricsSearchParams = useMemo(() => {
    if (!debouncedSearchValue || !categorySupportsAllMetricsSearch(selectedCategory)) {
      return null;
    }
    return getQueryParamsForCategory(METRICS_CATEGORIES.ALL, debouncedSearchValue, 0, includeMetricsReleasePolicyCount);
  }, [selectedCategory, debouncedSearchValue, includeMetricsReleasePolicyCount]);

  const allMetricsSearchQuery = useInfiniteMetrics(
    {
      projectKey,
      params: allMetricsSearchParams ?? undefined,
    },
    {
      enabled: allMetricsSearchParams !== null,
      staleTime: 5 * 60 * 1000,
    } as Parameters<typeof useInfiniteMetrics>[1],
  );

  // Always fetch most used metrics (preloaded) with error state
  const mostUsedQuery = useMetrics(
    {
      projectKey,
      params: getQueryParamsForCategory('most-used', undefined, 0, includeMetricsReleasePolicyCount),
    },
    {
      staleTime: 5 * 60 * 1000,
    },
  );

  // Always fetch recently added metrics (preloaded) with error state
  const recentlyAddedQuery = useMetrics(
    {
      projectKey,
      params: getQueryParamsForCategory('recently-added', undefined, 0, includeMetricsReleasePolicyCount),
    },
    {
      staleTime: 5 * 60 * 1000,
    },
  );

  // Release guardrails: metrics and groups used in release policies, paginate until all loaded
  // Only query when BOTH flags are true (AND)
  const releaseGuardrailsMetricsParams = useMemo(
    () =>
      getQueryParamsForCategory(METRICS_CATEGORIES.RELEASE_GUARDRAILS, undefined, 0, includeMetricsReleasePolicyCount),
    [includeMetricsReleasePolicyCount],
  );
  const releaseGuardrailsGroupsParams = useMemo(
    () =>
      getMetricGroupsQueryParams(
        METRICS_CATEGORIES.RELEASE_GUARDRAILS,
        undefined,
        0,
        includeMetricGroupsReleasePolicyCount,
      ),
    [includeMetricGroupsReleasePolicyCount],
  );

  const releaseGuardrailsMetricsQuery = useInfiniteMetrics(
    {
      projectKey,
      params: releaseGuardrailsMetricsParams,
    },
    {
      enabled: releaseGuardrailsEnabled && allowMetrics,
      staleTime: 5 * 60 * 1000,
    } as Parameters<typeof useInfiniteMetrics>[1],
  );

  const releaseGuardrailsGroupsQuery = useInfiniteMetricGroups(
    {
      projectKey,
      params: releaseGuardrailsGroupsParams,
    },
    {
      enabled: releaseGuardrailsEnabled && allowMetricGroups,
      staleTime: 5 * 60 * 1000,
    } as Parameters<typeof useInfiniteMetricGroups>[1],
  );

  return {
    infiniteMetricsQuery,
    infiniteMetricGroupsQuery,
    allMetricsSearchQuery,
    mostUsedQuery,
    recentlyAddedQuery,
    releaseGuardrailsMetricsQuery,
    releaseGuardrailsGroupsQuery,
    // Computed values for convenience
    hasAnyError:
      !!infiniteMetricsQuery.error ||
      !!infiniteMetricGroupsQuery.error ||
      !!allMetricsSearchQuery.error ||
      !!mostUsedQuery.error ||
      !!recentlyAddedQuery.error ||
      !!releaseGuardrailsMetricsQuery.error ||
      !!releaseGuardrailsGroupsQuery.error,
  };
};

// Custom hook to handle item processing with infinite queries
const useProcessedItems = (
  selectedCategory: string,
  dataQueries: ReturnType<typeof useMetricsData>,
  defaultMetrics: Metric[],
  defaultGroups: MetricGroupRep[],
  crossSearchActive: boolean,
) => {
  const mostUsedMetricsResponse = dataQueries.mostUsedQuery.data;
  const recentlyAddedMetricsResponse = dataQueries.recentlyAddedQuery.data;
  const releaseGuardrailsMetricsPages = dataQueries.releaseGuardrailsMetricsQuery.data?.pages;
  const releaseGuardrailsGroupsPages = dataQueries.releaseGuardrailsGroupsQuery.data?.pages;

  // Extract data from infinite query pages wrapped in useMemo to prevent recomputation
  const infiniteMetricsData = useMemo(() => {
    return dataQueries.infiniteMetricsQuery.data?.pages.flatMap((page) => page.items ?? []) ?? [];
  }, [dataQueries.infiniteMetricsQuery.data?.pages]);

  const infiniteMetricGroupsData = useMemo(() => {
    return dataQueries.infiniteMetricGroupsQuery.data?.pages.flatMap((page) => page.items ?? []) ?? [];
  }, [dataQueries.infiniteMetricGroupsQuery.data?.pages]);

  const allMetricsSearchResults = useMemo(() => {
    return dataQueries.allMetricsSearchQuery.data?.pages.flatMap((page) => page.items ?? []) ?? [];
  }, [dataQueries.allMetricsSearchQuery.data?.pages]);

  // Process metrics for the current category
  const metrics = useMemo(() => {
    if (selectedCategory === 'metric-groups' || selectedCategory === 'funnel-groups') {
      return [];
    }

    let baseResults: Metric[] = [];

    if (selectedCategory === 'most-used') {
      // Use preloaded most used metrics
      baseResults = mostUsedMetricsResponse?.items ?? [];
    } else if (selectedCategory === 'recently-added') {
      // Use preloaded recently added metrics
      baseResults = recentlyAddedMetricsResponse?.items ?? [];
    } else if (selectedCategory === 'release-guardrails') {
      // Use preloaded release guardrails metrics (used in guarded rollouts)
      baseResults = releaseGuardrailsMetricsPages?.flatMap((page) => page.items ?? []) ?? [];
    } else if (categorySupportsInfiniteScroll(selectedCategory)) {
      // For infinite scroll categories, use pages data
      baseResults = infiniteMetricsData;
    }

    // Only combine with default metrics if we're in currently-selected category
    if (selectedCategory === 'currently-selected') {
      const allMetrics = [...baseResults, ...defaultMetrics];
      const uniqueMetrics = allMetrics.filter(
        (metric, index, array) => array.findIndex((m) => m.key === metric.key) === index,
      );
      return uniqueMetrics;
    } else {
      // For other categories, just use the base results
      return baseResults;
    }
  }, [
    selectedCategory,
    mostUsedMetricsResponse,
    recentlyAddedMetricsResponse,
    releaseGuardrailsMetricsPages,
    infiniteMetricsData,
    defaultMetrics,
  ]);

  // All available metrics for selection logic (always includes ALL metrics we've seen)
  const allAvailableMetrics = useMemo(() => {
    const mostUsedMetrics = mostUsedMetricsResponse?.items ?? [];
    const recentlyAddedMetrics = recentlyAddedMetricsResponse?.items ?? [];
    const releaseGuardrailsMetrics = releaseGuardrailsMetricsPages?.flatMap((page) => page.items ?? []) ?? [];
    const allSeenMetrics = [
      ...infiniteMetricsData,
      ...allMetricsSearchResults,
      ...mostUsedMetrics,
      ...recentlyAddedMetrics,
      ...releaseGuardrailsMetrics,
      ...defaultMetrics,
    ];
    return allSeenMetrics.filter(
      (metric, index, array) => metric && array.findIndex((m) => m && m.key === metric.key) === index,
    );
  }, [
    infiniteMetricsData,
    allMetricsSearchResults,
    mostUsedMetricsResponse,
    recentlyAddedMetricsResponse,
    releaseGuardrailsMetricsPages,
    defaultMetrics,
  ]);

  // All available metric groups for selection logic (always includes selected ones)
  const allAvailableMetricGroups = useMemo(() => {
    const releaseGuardrailsGroups = releaseGuardrailsGroupsPages?.flatMap((page) => page.items ?? []) ?? [];
    const allGroups = [...infiniteMetricGroupsData, ...releaseGuardrailsGroups, ...defaultGroups];
    return allGroups.filter((group, index, array) => array.findIndex((g) => g.key === group.key) === index);
  }, [infiniteMetricGroupsData, releaseGuardrailsGroupsPages, defaultGroups]);

  // Metric groups to display in UI (only show default groups in currently-selected category)
  const metricGroups = useMemo(() => {
    if (selectedCategory === 'metric-groups' || selectedCategory === 'funnel-groups') {
      // For metric groups category, show infinite query results
      return infiniteMetricGroupsData;
    } else if (selectedCategory === 'currently-selected') {
      // For currently selected, show all available groups (includes defaults)
      return allAvailableMetricGroups;
    } else if (selectedCategory === 'release-guardrails') {
      // For release guardrails, show preloaded groups used in guarded rollouts
      return releaseGuardrailsGroupsPages?.flatMap((page) => page.items ?? []) ?? [];
    } else {
      return [];
    }
  }, [selectedCategory, infiniteMetricGroupsData, allAvailableMetricGroups, releaseGuardrailsGroupsPages]);

  // hasMore logic: use infinite query's hasNextPage
  const hasMore = useMemo(() => {
    const crossSearchHasMore = crossSearchActive && (dataQueries.allMetricsSearchQuery.hasNextPage ?? false);

    if (!categorySupportsInfiniteScroll(selectedCategory)) {
      return crossSearchHasMore;
    }

    if (selectedCategory === 'metric-groups' || selectedCategory === 'funnel-groups') {
      return dataQueries.infiniteMetricGroupsQuery.hasNextPage ?? false;
    } else {
      return (dataQueries.infiniteMetricsQuery.hasNextPage ?? false) || crossSearchHasMore;
    }
  }, [
    selectedCategory,
    crossSearchActive,
    dataQueries.infiniteMetricGroupsQuery.hasNextPage,
    dataQueries.infiniteMetricsQuery.hasNextPage,
    dataQueries.allMetricsSearchQuery.hasNextPage,
  ]);

  return {
    metrics,
    metricGroups,
    allMetricsSearchResults,
    allAvailableMetrics,
    allAvailableMetricGroups,
    hasMore,
  };
};

// Custom hook to handle selection logic
const useSelectionHandler = (
  allAvailableMetrics: Metric[],
  allAvailableMetricGroups: MetricGroupRep[],
  selectedKeys: Set<string>,
  baseSelectedKeys: Set<string>,
  selectionMode: 'single' | 'multiple',
  isUnavailable?: (item: MetricOrGroup) => string | undefined,
  onSelectionChange?: (selection: MetricSelection) => void,
  setSearchResultsKey?: (value: React.SetStateAction<number>) => void,
  trackingContext?: {
    onTrackFirstInteraction: (interactionType: 'select') => void;
    selectedCategory: string;
    searchValue: string;
    categories: Array<Array<{ key: string; name: string }>>;
  },
) => {
  const isItemDisabled = useCallback(
    (item: MetricOrGroup): boolean => {
      // Check if unavailable
      if (isUnavailable && !!isUnavailable(item)) {
        return true;
      }

      // For metrics, check if they're part of a selected group
      if (!isMetricGroup(item)) {
        return allAvailableMetricGroups
          .filter((g) => selectedKeys.has(g.key))
          .some((group) => group.metrics.some((m) => m.key === item.key));
      }

      return false;
    },
    [allAvailableMetricGroups, selectedKeys, isUnavailable],
  );

  const handleSelectionChange = useCallback(
    (newSelection: Selection) => {
      if (newSelection === 'all') {
        return;
      }

      // Handle empty selection (unselect all)
      if (newSelection instanceof Set && newSelection.size === 0) {
        // Track unselect all action
        trackMetricSelectionUnselectAllClicked();
        metricsSearchTracker.track('Unselect All Clicked');

        // Force SearchResults to re-render when unselect all is triggered
        // this is necessary to ensure that the disabled items are displayed with updated styling immediately
        setSearchResultsKey?.((prev) => prev + 1);

        const selection: MetricSelection = {
          selectedMetrics: [],
          selectedMetricGroups: [],
        };
        if (onSelectionChange) {
          onSelectionChange(selection);
        }
        return;
      }

      // For single selection mode, handle selection replacement directly
      if (selectionMode === 'single' && newSelection instanceof Set && newSelection.size > 0) {
        const newKey = String(Array.from(newSelection)[0]);

        // Check if the new item is disabled
        const metricGroup = allAvailableMetricGroups.find((mg) => mg.key === newKey);
        const metric = allAvailableMetrics.find((m) => m.key === newKey);
        const item = metricGroup || metric;

        if (item && isItemDisabled(item)) {
          return; // Don't select disabled items
        }

        // Track first interaction if this is a new selection (not already selected)
        if (trackingContext && !selectedKeys.has(newKey)) {
          trackingContext.onTrackFirstInteraction('select');

          // Track item selection
          const categoryName =
            trackingContext.categories.flat().find((cat) => cat.key === trackingContext.selectedCategory)?.name ||
            trackingContext.selectedCategory;
          const attributes = {
            itemType: metricGroup ? 'metricGroup' : 'metric',
            itemKey: newKey,
            categoryName,
            searchQuery: trackingContext.searchValue,
          } as const;
          trackMetricSelectionItemSelected(attributes);
          metricsSearchTracker.track('Item Selected', attributes);
        }

        // Build selection with only the new item
        const selectedMetrics: Metric[] = [];
        const selectedMetricGroups: MetricGroupRep[] = [];

        if (metricGroup) {
          selectedMetricGroups.push(metricGroup);
        } else if (metric) {
          selectedMetrics.push(metric);
        }

        const selection: MetricSelection = {
          selectedMetrics,
          selectedMetricGroups,
        };

        if (onSelectionChange) {
          onSelectionChange(selection);
        }
        return;
      }

      // Find what items were newly added or removed by comparing with current selection
      const currentSelectionKeys = new Set(Array.from(selectedKeys));
      const newSelectionKeys = new Set(Array.from(newSelection).map(String));

      const newlyAddedKeys = Array.from(newSelection).filter((key) => !currentSelectionKeys.has(String(key)));
      const removedKeys = Array.from(selectedKeys).filter((key) => !newSelectionKeys.has(String(key)));

      // Check if any newly added OR removed items are disabled
      const allChangedKeys = [...newlyAddedKeys, ...removedKeys];
      const hasDisabledChangedItems = allChangedKeys.some((key) => {
        const keyString = String(key);
        const metricGroup = allAvailableMetricGroups.find((mg) => mg.key === keyString);
        const metric = allAvailableMetrics.find((m) => m.key === keyString);
        const item = metricGroup || metric;
        return item ? isItemDisabled(item) : false;
      });

      // If any changed items are disabled, don't proceed at all
      if (hasDisabledChangedItems) {
        return;
      }

      // Track newly added items
      if (trackingContext && newlyAddedKeys.length > 0) {
        trackingContext.onTrackFirstInteraction('select');

        const categoryName =
          trackingContext.categories.flat().find((cat) => cat.key === trackingContext.selectedCategory)?.name ||
          trackingContext.selectedCategory;

        // Track each newly added item
        newlyAddedKeys.forEach((key) => {
          const keyString = String(key);
          const metricGroup = allAvailableMetricGroups.find((mg) => mg.key === keyString);
          const metric = allAvailableMetrics.find((m) => m.key === keyString);

          if (metricGroup || metric) {
            const attributes = {
              itemType: metricGroup ? 'metricGroup' : 'metric',
              itemKey: keyString,
              categoryName,
              searchQuery: trackingContext.searchValue,
            } as const;
            trackMetricSelectionItemSelected(attributes);
            metricsSearchTracker.track('Item Selected', attributes);
          }
        });
      }

      // Filter selection to deduplicate metrics that are part of selected groups
      const filteredSelection = new Set<string>();

      // First pass: Add all groups to filteredSelection
      newSelection.forEach((key) => {
        const keyString = String(key);
        if (allAvailableMetricGroups.some((g) => g.key === keyString)) {
          filteredSelection.add(keyString);
        }
      });

      // Second pass: Add metrics that aren't part of any selected group
      newSelection.forEach((key) => {
        const keyString = String(key);

        // Skip if it's a group (already handled above)
        if (allAvailableMetricGroups.some((g) => g.key === keyString)) {
          return;
        }

        // Only include metrics that aren't part of a selected group
        const metric = allAvailableMetrics.find((m) => m.key === keyString);
        if (metric) {
          const isPartOfSelectedGroup = allAvailableMetricGroups
            .filter((g) => filteredSelection.has(g.key) || baseSelectedKeys.has(g.key))
            .some((group) => group.metrics?.some((m) => m.key === keyString));

          if (!isPartOfSelectedGroup) {
            filteredSelection.add(keyString);
          }
        }
      });

      // Build the final selection for the callback
      const selectedMetrics: Metric[] = [];
      const selectedMetricGroups: MetricGroupRep[] = [];

      filteredSelection.forEach((keyString) => {
        const metricGroup = allAvailableMetricGroups.find((mg) => mg.key === keyString);
        if (metricGroup) {
          selectedMetricGroups.push(metricGroup);
          return;
        }

        const metric = allAvailableMetrics.find((m) => m.key === keyString);
        if (metric) {
          selectedMetrics.push(metric);
        }
      });

      const selection: MetricSelection = {
        selectedMetrics,
        selectedMetricGroups,
      };

      if (onSelectionChange) {
        onSelectionChange(selection);
      }
    },
    [
      allAvailableMetrics,
      allAvailableMetricGroups,
      selectedKeys,
      baseSelectedKeys,
      isItemDisabled,
      onSelectionChange,
      setSearchResultsKey,
      selectionMode,
      trackingContext,
    ],
  );

  return { handleSelectionChange, isItemDisabled };
};

// Custom hook to handle filtered items logic
const useFilteredItems = (
  selectedCategory: string,
  debouncedSearchValue: string,
  metrics: Metric[],
  metricGroups: MetricGroupRep[],
  allAvailableMetrics: Metric[],
  allAvailableMetricGroups: MetricGroupRep[],
  selectedKeys: Set<string>,
  allowMetrics: boolean,
  allowMetricGroups: boolean,
  allMetricsSearchResults: Metric[],
  crossSearchActive: boolean,
) => {
  // Helper function to filter items by search
  const filterItemsBySearch = useCallback((items: MetricOrGroup[], searchQuery: string): MetricOrGroup[] => {
    if (!searchQuery) {
      return items;
    }
    const searchLower = searchQuery.toLowerCase();
    return items.filter(
      (item) => item.name.toLowerCase().includes(searchLower) || item.key.toLowerCase().includes(searchLower),
    );
  }, []);

  // Helper function to get category display name
  const getCategoryDisplayName = useCallback((category: string): string => {
    switch (category) {
      case 'most-used':
        return msg('metrics.metricsSearch.mostUsedCategory');
      case 'recently-added':
        return msg('metrics.metricsSearch.recentlyAddedCategory');
      case 'autogenerated':
        return msg('metrics.metricsSearch.autogeneratedCategory');
      case 'agent-control':
        return msg('metrics.metricsSearch.agentControlCategory');
      case 'user-defined':
        return msg('metrics.metricsSearch.userDefinedCategory');
      case 'warehouse-native':
        return msg('metrics.metricsSearch.warehouseNativeCategory');
      default:
        return category;
    }
  }, []);

  // Combine metrics and metric groups based on selected category
  const filteredItems = useMemo(() => {
    if (selectedCategory === 'currently-selected') {
      // For currently selected, combine both selected metrics and groups
      // Exclude metrics that are part of selected groups (they're auto-expanded, not user-selected)
      const currentlySelectedMetrics: Metric[] = allAvailableMetrics.filter((m) => {
        if (!selectedKeys.has(m.key)) {
          return false;
        }

        // Check if this metric is part of any selected group
        const isPartOfSelectedGroup = allAvailableMetricGroups
          .filter((g) => selectedKeys.has(g.key))
          .some((group) => group.metrics?.some((gm) => gm.key === m.key));

        return !isPartOfSelectedGroup; // Only include if NOT part of a selected group
      });
      const currentlySelectedGroups: MetricGroupRep[] = allAvailableMetricGroups.filter((g) => selectedKeys.has(g.key));

      // Always show all selected items regardless of search query
      return [...currentlySelectedMetrics, ...currentlySelectedGroups];
    } else if (selectedCategory === 'metric-groups' || selectedCategory === 'funnel-groups') {
      return metricGroups;
    } else if (selectedCategory === 'release-guardrails') {
      // Release guardrails: combine metrics and groups, search filtered on frontend
      const metricsToShow = debouncedSearchValue ? filterItemsBySearch(metrics, debouncedSearchValue) : metrics;
      const groupsToShow = debouncedSearchValue
        ? filterItemsBySearch(metricGroups, debouncedSearchValue)
        : metricGroups;
      const sections: Array<{ name: string; items: Iterable<MetricOrGroup> }> = [];
      if (allowMetrics && metricsToShow.length > 0) {
        sections.push({ name: msg('metrics.metricsSearch.metricsSection'), items: metricsToShow });
      }
      if (allowMetricGroups && groupsToShow.length > 0) {
        sections.push({ name: msg('metrics.metricsSearch.metricGroupsSection'), items: groupsToShow });
      }
      return sections.length > 0 ? sections : [];
    } else if (crossSearchActive) {
      // Cross-category search: matches within the current category on top, matches from
      // the rest of the catalog below so metrics outside the category still show up
      const currentCategoryName = getCategoryDisplayName(selectedCategory);

      // Most used / Recently added are preloaded without the search applied, so filter
      // client-side; the other categories are already server-filtered by the search
      const currentCategoryMatches =
        selectedCategory === 'most-used' || selectedCategory === 'recently-added'
          ? filterItemsBySearch(metrics, debouncedSearchValue)
          : metrics;
      const currentCategoryKeys = new Set(currentCategoryMatches.map((item) => item.key));
      const allMetricsMatches = allMetricsSearchResults.filter((m) => !currentCategoryKeys.has(m.key));

      const sections: Array<{ name: string; items: Iterable<MetricOrGroup> }> = [];

      if (currentCategoryMatches.length > 0) {
        sections.push({
          name: msg('metrics.metricsSearch.relevantMatchesIn', { category: currentCategoryName }),
          items: currentCategoryMatches,
        });
      } else {
        sections.push({
          name: msg('metrics.metricsSearch.noMatchesIn', { category: currentCategoryName }),
          items: [],
        });
      }

      if (allMetricsMatches.length > 0) {
        sections.push({
          name: msg('metrics.metricsSearch.relevantMatchesInAll'),
          items: allMetricsMatches,
        });
      }

      return sections;
    } else {
      return metrics;
    }
  }, [
    selectedCategory,
    debouncedSearchValue,
    metrics,
    metricGroups,
    allAvailableMetrics,
    allAvailableMetricGroups,
    selectedKeys,
    allowMetrics,
    allowMetricGroups,
    allMetricsSearchResults,
    crossSearchActive,
    filterItemsBySearch,
    getCategoryDisplayName,
  ]);

  return filteredItems;
};

// Custom hook to handle all UI state and computed values
const useMetricsSearchState = (
  baseSelectedKeys: Set<string>,
  allAvailableMetrics: Metric[],
  allAvailableMetricGroups: MetricGroupRep[],
  selectedCategory: string,
  selectionMode: 'single' | 'multiple',
  isInitialLoad: React.RefObject<boolean>,
  isLoadingDefaults: boolean,
  dataQueries: ReturnType<typeof useMetricsData>,
  crossSearchActive: boolean,
  isUnavailable?: (item: MetricOrGroup) => string | undefined,
  onSelectionChange?: (selection: MetricSelection) => void,
  setSearchResultsKey?: (value: React.SetStateAction<number>) => void,
  trackingContext?: {
    onTrackFirstInteraction: (interactionType: 'select') => void;
    searchValue: string;
    categories: Array<Array<{ key: string; name: string }>>;
  },
) => {
  // Expand selectedKeys to include metrics from selected groups (for visual consistency)
  const selectedKeys = useMemo(() => {
    const expandedKeys = new Set(baseSelectedKeys);
    allAvailableMetricGroups
      .filter((group) => baseSelectedKeys.has(group.key))
      .forEach((group) => {
        group.metrics?.forEach((metric) => {
          expandedKeys.add(metric.key);
        });
      });
    return expandedKeys;
  }, [baseSelectedKeys, allAvailableMetricGroups]);

  // Selection logic with all complex handling
  const { handleSelectionChange, isItemDisabled } = useSelectionHandler(
    allAvailableMetrics,
    allAvailableMetricGroups,
    selectedKeys,
    baseSelectedKeys,
    selectionMode,
    isUnavailable,
    onSelectionChange,
    setSearchResultsKey,
    trackingContext
      ? {
          ...trackingContext,
          selectedCategory,
        }
      : undefined,
  );

  // Loading states
  const searchResultsLoading = useMemo(() => {
    // Always show loading during initial load with defaults
    if (isInitialLoad.current && isLoadingDefaults) {
      return true;
    }

    // Never show loading for currently-selected (it's always instant)
    if (selectedCategory === 'currently-selected') {
      return false;
    }

    // Wait for the cross-category search so its section doesn't pop in after the fact
    const crossSearchLoading = crossSearchActive && dataQueries.allMetricsSearchQuery.isLoading;

    // For infinite scroll categories, show loading only on initial page load
    if (categorySupportsInfiniteScroll(selectedCategory)) {
      if (selectedCategory === 'metric-groups' || selectedCategory === 'funnel-groups') {
        return dataQueries.infiniteMetricGroupsQuery.isLoading;
      } else {
        return dataQueries.infiniteMetricsQuery.isLoading || crossSearchLoading;
      }
    }

    // Determine loading state based on category for non-infinite queries
    const isLoading =
      selectedCategory === 'most-used'
        ? dataQueries.mostUsedQuery.isLoading
        : selectedCategory === 'recently-added'
          ? dataQueries.recentlyAddedQuery.isLoading
          : selectedCategory === 'release-guardrails'
            ? dataQueries.releaseGuardrailsMetricsQuery.isLoading ||
              dataQueries.releaseGuardrailsGroupsQuery.isLoading ||
              dataQueries.releaseGuardrailsMetricsQuery.isFetchingNextPage ||
              dataQueries.releaseGuardrailsGroupsQuery.isFetchingNextPage
            : false;

    return isLoading || crossSearchLoading;
  }, [isInitialLoad, isLoadingDefaults, selectedCategory, dataQueries, crossSearchActive]);

  return {
    selectedKeys,
    handleSelectionChange,
    isItemDisabled,
    searchResultsLoading,
  };
};

// Internal component that uses suspense hooks
function MetricsSearchInternal({
  selectionMode = 'multiple',
  defaultSelectedMetricKeys = [],
  defaultSelectedMetricGroupKeys = [],
  defaultCategory,
  isUnavailable,
  allowMetrics = true,
  allowMetricGroups = true,
  allowWarehouseNativeMetrics = false,
  scopeByEnvironment = false,
  onSelectionChange,
  onCreateMetric,
  onCreateMetricGroup,
  disableCreate = false,
  expectedIntegrationKey,
  onDone,
  hideDoneButton = false,
}: MetricsSearchProps) {
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const didSelectRef = useRef(false);

  useEffect(() => {
    metricsSearchTracker.start();

    return () => {
      metricsSearchTracker.end({ outcome: didSelectRef.current ? 'success' : 'cancel' });
    };
  }, []);

  const projectKey = useProjectKey();
  const environmentKey = useSelectedEnvironmentKey();

  const searchInputRef = useRef<HTMLInputElement>(null);

  // Release guardrails category is hidden from the metric selector
  const releaseGuardrailsEnabled = false;

  // Get dynamic categories based on props (memoized to prevent unnecessary re-renders)
  const categories = useMemo(
    () => getCategories(allowMetrics, allowMetricGroups, allowWarehouseNativeMetrics, releaseGuardrailsEnabled),
    [allowMetrics, allowMetricGroups, allowWarehouseNativeMetrics, releaseGuardrailsEnabled],
  );

  // Determine default category based on props and selections
  const initialCategory = useMemo(
    () =>
      getDefaultCategory(
        defaultCategory,
        defaultSelectedMetricKeys.length > 0 || defaultSelectedMetricGroupKeys.length > 0,
        allowMetrics,
        allowMetricGroups,
        releaseGuardrailsEnabled,
      ),
    [
      defaultCategory,
      defaultSelectedMetricKeys.length,
      defaultSelectedMetricGroupKeys.length,
      allowMetrics,
      allowMetricGroups,
      releaseGuardrailsEnabled,
    ],
  );

  const [selectedCategory, setSelectedCategory] = useState<string>(initialCategory);

  // Reset category if release-guardrails is selected but category is now hidden
  useEffect(() => {
    if (selectedCategory === METRICS_CATEGORIES.RELEASE_GUARDRAILS && !releaseGuardrailsEnabled) {
      setSelectedCategory(
        getDefaultCategory(
          undefined,
          defaultSelectedMetricKeys.length > 0 || defaultSelectedMetricGroupKeys.length > 0,
          allowMetrics,
          allowMetricGroups,
          false,
        ),
      );
    }
  }, [
    selectedCategory,
    releaseGuardrailsEnabled,
    allowMetrics,
    allowMetricGroups,
    defaultSelectedMetricKeys.length,
    defaultSelectedMetricGroupKeys.length,
  ]);
  const [searchResultsKey, setSearchResultsKey] = useState(0);

  // Use the provided keys directly - trust they are well-formed
  const baseSelectedKeys = new Set([...defaultSelectedMetricKeys, ...defaultSelectedMetricGroupKeys]);

  // Track initial load phase
  const isInitialLoad = useRef(true);

  // Track first interaction
  const hasTrackedFirstInteraction = useRef(false);

  // Debounce search input to avoid excessive API calls
  const debouncedSetSearchValue = useDebounce((value: string) => setDebouncedSearchValue(value), 300);

  // Update debounced value when search value changes
  useEffect(() => {
    debouncedSetSearchValue(searchValue);
  }, [searchValue, debouncedSetSearchValue]);

  // Track search queries when debounced value changes
  useEffect(() => {
    if (debouncedSearchValue) {
      const attributes = { searchQuery: debouncedSearchValue };
      trackMetricSelectionSearchQueried(attributes);
      metricsSearchTracker.track('Search Queried', attributes);
    }
  }, [debouncedSearchValue]);

  const crossSearchActive = !!debouncedSearchValue && categorySupportsAllMetricsSearch(selectedCategory);

  // Data fetching with infinite queries
  const dataQueries = useMetricsData(
    projectKey,
    selectedCategory,
    debouncedSearchValue,
    allowMetrics,
    allowMetricGroups,
    releaseGuardrailsEnabled,
    true,
    true,
  );

  // Release guardrails: fetch all pages until we have everything
  useEffect(() => {
    if (!releaseGuardrailsEnabled) {
      return;
    }

    // Bail out on error to prevent infinite retry loop
    const metricsHasError =
      dataQueries.releaseGuardrailsMetricsQuery.isError ||
      dataQueries.releaseGuardrailsMetricsQuery.isFetchNextPageError;
    const groupsHasError =
      dataQueries.releaseGuardrailsGroupsQuery.isError || dataQueries.releaseGuardrailsGroupsQuery.isFetchNextPageError;

    const fetchAllMetrics =
      !metricsHasError &&
      dataQueries.releaseGuardrailsMetricsQuery.hasNextPage &&
      !dataQueries.releaseGuardrailsMetricsQuery.isFetchingNextPage;
    const fetchAllGroups =
      !groupsHasError &&
      dataQueries.releaseGuardrailsGroupsQuery.hasNextPage &&
      !dataQueries.releaseGuardrailsGroupsQuery.isFetchingNextPage;

    if (fetchAllMetrics && allowMetrics) {
      void dataQueries.releaseGuardrailsMetricsQuery.fetchNextPage();
    }
    if (fetchAllGroups && allowMetricGroups) {
      void dataQueries.releaseGuardrailsGroupsQuery.fetchNextPage();
    }
  }, [
    releaseGuardrailsEnabled,
    allowMetrics,
    allowMetricGroups,
    dataQueries.releaseGuardrailsMetricsQuery,
    dataQueries.releaseGuardrailsGroupsQuery,
  ]);

  // Add error handling for the useDefaultSelectedItems hook
  const {
    defaultMetrics,
    defaultGroups,
    isLoading: isLoadingDefaults,
    isError: defaultsError,
  } = useDefaultSelectedItems(defaultSelectedMetricKeys, defaultSelectedMetricGroupKeys);

  // Determine if there is an error
  const hasError = dataQueries.hasAnyError || !!defaultsError;

  // Item processing with infinite queries
  const processedItems = useProcessedItems(
    selectedCategory,
    dataQueries,
    defaultMetrics,
    defaultGroups,
    crossSearchActive,
  );

  const { metrics, metricGroups, allMetricsSearchResults, allAvailableMetrics, allAvailableMetricGroups, hasMore } =
    processedItems;

  // Track first interaction callback
  const trackFirstInteraction = useCallback((interactionType: 'search' | 'category' | 'select' | 'create') => {
    if (!hasTrackedFirstInteraction.current) {
      trackMetricSelectionFirstInteraction({ interactionType });
      hasTrackedFirstInteraction.current = true;
    }
  }, []);

  // All UI state and computed values
  const { selectedKeys, handleSelectionChange, isItemDisabled, searchResultsLoading } = useMetricsSearchState(
    baseSelectedKeys,
    allAvailableMetrics,
    allAvailableMetricGroups,
    selectedCategory,
    selectionMode,
    isInitialLoad,
    isLoadingDefaults,
    dataQueries,
    crossSearchActive,
    isUnavailable,
    onSelectionChange,
    setSearchResultsKey,
    {
      onTrackFirstInteraction: trackFirstInteraction,
      searchValue,
      categories,
    },
  );

  // Filtered items
  const filteredItems = useFilteredItems(
    selectedCategory,
    debouncedSearchValue,
    metrics,
    metricGroups,
    allAvailableMetrics,
    allAvailableMetricGroups,
    selectedKeys,
    allowMetrics,
    allowMetricGroups,
    allMetricsSearchResults,
    crossSearchActive,
  );

  // loadMore function: use infinite query's fetchNextPage
  const handleLoadMore = async () => {
    if (!categorySupportsInfiniteScroll(selectedCategory) && !crossSearchActive) {
      return;
    }

    if (selectedCategory === 'metric-groups' || selectedCategory === 'funnel-groups') {
      // Prevent multiple simultaneous requests
      if (!dataQueries.infiniteMetricGroupsQuery.isFetchingNextPage) {
        await dataQueries.infiniteMetricGroupsQuery.fetchNextPage();
      }
    } else if (categorySupportsInfiniteScroll(selectedCategory) && dataQueries.infiniteMetricsQuery.hasNextPage) {
      // Prevent multiple simultaneous requests
      if (!dataQueries.infiniteMetricsQuery.isFetchingNextPage) {
        await dataQueries.infiniteMetricsQuery.fetchNextPage();
      }
    } else if (crossSearchActive && dataQueries.allMetricsSearchQuery.hasNextPage) {
      // Once the current category is exhausted, page the cross-category search results
      if (!dataQueries.allMetricsSearchQuery.isFetchingNextPage) {
        await dataQueries.allMetricsSearchQuery.fetchNextPage();
      }
    }
  };

  // Get isLoadingMore from infinite queries
  const isLoadingMore =
    selectedCategory === 'metric-groups' || selectedCategory === 'funnel-groups'
      ? dataQueries.infiniteMetricGroupsQuery.isFetchingNextPage
      : dataQueries.infiniteMetricsQuery.isFetchingNextPage || dataQueries.allMetricsSearchQuery.isFetchingNextPage;

  // Mark that initial load is complete after first render
  useEffect(() => {
    isInitialLoad.current = false;
  }, []);

  // Auto-focus the search input when the component mounts
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  const renderItemDetails = (item: MetricOrGroup | null) => {
    if (!item) {
      return (
        <div className={styles.loadingContainer}>
          <ProgressBar size="small" isIndeterminate aria-label={msg('metrics.metricsSearch.loadingDetailsLabel')} />
        </div>
      );
    }

    // Use type guard to determine which panel to show
    if (isMetricGroup(item)) {
      return (
        <MetricGroupDetailsPanel
          key={item.key}
          metricGroup={item}
          unavailableReason={isUnavailable && isUnavailable(item)}
        />
      );
    }

    return (
      <MetricDetailsPanel
        key={item.key}
        metric={item}
        unavailableReason={isUnavailable && isUnavailable(item)}
        scopeByEnvironment={scopeByEnvironment}
      />
    );
  };

  const handleCategorySelection = (keys: Selection) => {
    if (keys !== 'all' && keys.size > 0) {
      const categoryKey = Array.from(keys)[0] as string;

      trackFirstInteraction('category');

      // Track category selection
      const categoryName = categories.flat().find((cat) => cat.key === categoryKey)?.name || categoryKey;
      const attributes = { categoryName } as const;
      trackMetricSelectionCategorySelected(attributes);
      metricsSearchTracker.track('Category Selected', attributes);

      setSelectedCategory(categoryKey);
    }
  };

  const handleSearchChange = (value: string) => {
    if (value.length > 0) {
      trackFirstInteraction('search');
    }

    setSearchValue(value);

    if (value.length > 0) {
      const categoriesFlat = categories.flat();
      if (selectedCategory === METRICS_CATEGORIES.CURRENTLY_SELECTED) {
        if (categoriesFlat.some((cat) => cat.key === METRICS_CATEGORIES.ALL)) {
          setSelectedCategory(METRICS_CATEGORIES.ALL);
        } else if (categoriesFlat.some((cat) => cat.key === METRICS_CATEGORIES.METRIC_GROUPS)) {
          setSelectedCategory(METRICS_CATEGORIES.METRIC_GROUPS);
        } else if (categoriesFlat.some((cat) => cat.key === METRICS_CATEGORIES.FUNNEL_GROUPS)) {
          setSelectedCategory(METRICS_CATEGORIES.FUNNEL_GROUPS);
        }
      }
    }
  };

  // Memoize custom selected count to prevent inline calculation
  const customSelectedCount = useMemo(() => {
    return Array.from(selectedKeys).filter((key) => allAvailableMetrics.some((m) => m.key === key)).length;
  }, [selectedKeys, allAvailableMetrics]);

  // Memoize renderEmptyState callback to prevent inline function creation
  const renderEmptyStateCallback = useCallback(() => {
    return (
      <MetricsSearchEmptyState
        category={selectedCategory}
        searchValue={debouncedSearchValue}
        projectKey={projectKey}
        environmentKey={environmentKey}
        disableCreate={disableCreate}
        expectedIntegrationKey={expectedIntegrationKey}
        onCategoryChange={setSelectedCategory}
        onCreateMetric={onCreateMetric}
        onCreateMetricGroup={onCreateMetricGroup}
      />
    );
  }, [
    selectedCategory,
    debouncedSearchValue,
    projectKey,
    environmentKey,
    disableCreate,
    expectedIntegrationKey,
    onCreateMetric,
    onCreateMetricGroup,
  ]);

  return (
    <div>
      <ResourceSelector
        selectedKeys={selectedKeys}
        onSelectionChange={(selection) => {
          if (selection instanceof Set) {
            didSelectRef.current = selection.size > 0;
          }
          handleSelectionChange(selection);
        }}
        renderDetails={renderItemDetails}
        renderEmptyState={renderEmptyStateCallback}
        resourceName="metric"
        customSelectedCount={customSelectedCount}
        selectionMode={selectionMode}
        aria-label={msg('metrics.metricsSearch.selectLabel')}
        hasMore={hasMore}
        onLoadMore={handleLoadMore}
        isLoadingMore={isLoadingMore}
        onDone={onDone}
        hideDoneButton={hideDoneButton}
      >
        {/* Header with search and action */}
        <div slot="header">
          <Input
            ref={searchInputRef}
            placeholder={msg('metrics.metricsSearch.searchPlaceholder')}
            value={searchValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
          />
          {renderCreateAction(
            '+ Create',
            selectedCategory === 'metric-groups' || selectedCategory === 'funnel-groups' || !allowMetrics
              ? toCreateMetricGroup({ projectKey, environmentKey })
              : toCreateMetric({ projectKey, environmentKey }),
            selectedCategory === 'metric-groups' || selectedCategory === 'funnel-groups' || !allowMetrics
              ? onCreateMetricGroup
              : onCreateMetric,
            disableCreate,
            false, // Don't show external icon for header button
            () => {
              trackFirstInteraction('create');
            },
          )}
        </div>

        {/* Categories sidebar */}
        <SearchCategories
          items={categories}
          selectedKeys={new Set([selectedCategory])}
          onSelectionChange={handleCategorySelection}
          selectionMode="single"
        >
          {(category: { key: string; name: string }) => (
            <ListBoxItem key={category.key} id={category.key} selectionIndicatorPlacement="none">
              {category.name}
            </ListBoxItem>
          )}
        </SearchCategories>

        {/* Main results */}
        <SearchResults<MetricOrGroup>
          key={searchResultsKey}
          items={filteredItems as SearchResultsData<MetricOrGroup>}
          selectionMode={selectionMode}
          isLoading={searchResultsLoading}
          isError={hasError}
          aria-label={msg('metrics.metricsSearch.resultsLabel')}
          resultsAreaClassName={styles.searchResultsArea}
          onLoadMore={
            selectedCategory === 'currently-selected' || selectedCategory === 'release-guardrails'
              ? undefined
              : handleLoadMore
          }
          hasMore={
            selectedCategory === 'currently-selected' || selectedCategory === 'release-guardrails' ? false : hasMore
          }
        >
          {(item) => (
            <ListBoxItem
              key={item.key}
              id={item.key}
              textValue={isItemDisabled(item) ? `${item.name} (Unavailable)` : item.name}
              className={clsx({
                [styles.multiSelectItem]: selectionMode === 'multiple',
                [styles.itemBoxDisabled]: isItemDisabled(item),
              })}
            >
              {isMetricGroup(item) ? (
                <MetricGroupItemContent metricGroup={item} />
              ) : (
                <MetricItemContent metric={item} scopeByEnvironment={scopeByEnvironment} />
              )}
            </ListBoxItem>
          )}
        </SearchResults>
      </ResourceSelector>
    </div>
  );
}

// Main exported component
export function MetricsSearch(props: MetricsSearchProps) {
  return <MetricsSearchInternal {...props} />;
}
